import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Stepper, Step, StepLabel, StepContent, Box, Typography, CircularProgress, Paper, IconButton } from '@mui/material';
import { OndemandVideo, ArrowBack } from '@mui/icons-material';

import { calculateAge, calculateMaturationPHV, centileRankToMuscleStrength, getMinDOB } from "../utils/DiactiveUtils";
import {
  integerPattern, oneDecimalPattern, twoDecimalsPattern, NumberType,
  validateName, validateNickname, validateEmail,
  validateDate, validateDatesEquivalence,
  validateRequiredField, validateRequiredFields,
} from '../utils/ValidationUtils';
import { getAuthUserId } from '../services/AuthService';
import { sendResetCountdownDaysEmail } from '../managers/MailManager';
import { getPatientData, addPatient, updatePatient } from '../services/PatientService';
import { hasCompletedTutorials } from '../services/TutorialService';
import { determineCentileRank } from '../services/FunctionService';
import { DoctorRoles, PatientDataMode, Phase } from '../config/DiactiveTypes';
import FormField from '../components/patients/FormField';
import FormTitle from '../components/patients/FormTitle';
import StepIcon from '../components/patients/StepIcon';
import ConfirmDialog from '../components/common/ConfirmDialog';

import PatientDataConfig from '../data/PatientDataConfig.json';

const Steps = PatientDataConfig.steps;

const toDateInputValue = (value) =>
  value?.toDate ? value.toDate().toISOString().split("T")[0] : null;

const SetPatientData = ({ mode, role }) => {
  const navigate = useNavigate();
  const { phase, patientId } = useParams();
  const authUserId = getAuthUserId();

  const isAddingPatient = mode === PatientDataMode.Eval && phase === Phase.PRE;

  const initializeFormData = () => {
    const initialData = {};
    Steps.forEach(step => {
      step.fields.forEach(field => {
        initialData[field.name] = field.defaultValue !== undefined ? field.defaultValue : "";
      });
    });
    return initialData;
  };

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initializeFormData());
  const [fetchingCentileRank, setFetchingCentileRank] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTutorialsMessage, setShowTutorialsMessage] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [patientRole, setPatientRole] = useState("");
  const [checkedTutorials, setCheckedTutorials] = useState(!DoctorRoles.includes(role));
  const [loadedData, setLoadedData] = useState(isAddingPatient);
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const [isCountdownStartedAtRequired, setIsCountdownStartedAtRequired] = useState(false);
  const [initialCountdownStartedAt, setInitialCountdownStartedAt] = useState(null);
  const [patientForMail, setPatientForMail] = useState(null);

  useEffect(() => {
      const fetchPatient = async () => {
        try {
          const { userData, preEvalData, postEvalData } = await getPatientData(role, patientId);

          for (const key in userData) {
            const fieldConfig = findFieldConfig(key);
            if (fieldConfig && fieldConfig.collections.includes("eval"))
              delete userData[key];
          }

          const patientData = phase === Phase.PRE ? {...userData, ...preEvalData} : {...userData, ...postEvalData};
          const newFormData = {...formData};

          for (const key in patientData) {
            if (key in newFormData) {
              const value = patientData[key];
              if (key === "dateOfBirth" || key === "diseaseOnsetDate" || key === "countdownStartedAt") {
                newFormData[key] = toDateInputValue(value) ?? value;
              }
              else if (typeof value === "number") {
                newFormData[key] = value.toString().replace(".", ",");
              }
              else {
                newFormData[key] = value;
              }
            }
          }
          newFormData.confirmEmail = newFormData.email;
          setFormData(newFormData);
          setPatientRole(patientData.role);
          setUserCreatedAt(toDateInputValue(userData.createdAt));
          setIsCountdownStartedAtRequired(Boolean(userData.countdownStartedAt));
          setInitialCountdownStartedAt(toDateInputValue(userData.countdownStartedAt));
          setPatientForMail({
            doctor: userData.doctor,
            hospital: userData.hospital,
            patientNumber: userData.patientNumber,
            isInStudy: userData.isInStudy,
          });
        }
        catch (error) {
        }
        finally {
          setLoadedData(true);
        }
      };

      if (!isAddingPatient) fetchPatient();
    }, [role, patientId]);

  useEffect(() => {
    const checkTutorials = async () => {
      const completed = await hasCompletedTutorials(authUserId, role);
      if (!completed) {
        setShowTutorialsMessage(true);
      }
      setCheckedTutorials(true);
    };
    if (DoctorRoles.includes(role)) checkTutorials();
  }, [authUserId]);

  const { maturation, phv } = calculateMaturationPHV(formData.gender, new Date(formData.dateOfBirth), formData.height.replace(",", "."));
  const insulinCorrection = formData.basalInsulin && formData.totalInsulin ? formData.totalInsulin - formData.basalInsulin : "";
  const totalInsulinDose = formData.totalInsulin && formData.weight ? (
    (formData.totalInsulin / parseFloat(formData.weight.replace(",", "."))).toFixed(2).replace(".", ",")
  ) : "";

  let basalInsulinPercentage = 0;
  if (formData.basalInsulin) {
    basalInsulinPercentage = Math.round(formData.basalInsulin / parseFloat(formData.totalInsulin) * 100);
  }

  const validateAllSteps = () => {
    const allErrors = {};
    let firstErrorStep = null;

    Steps.forEach((_, stepIndex) => {
      const stepErrors = {};
      validateStep(stepIndex, stepErrors);

      if (Object.keys(stepErrors).length > 0 && firstErrorStep === null) {
        firstErrorStep = stepIndex; // Track the first step with errors
      }

      Object.assign(allErrors, stepErrors); // Merge errors
    });

    if (formData.muscleStrength === "") {
      allErrors.calculateMuscleStrength = "Debe calcular el nivel de fuerza muscular";
      if (firstErrorStep === null) {
        firstErrorStep = Steps.length - 1; // Set to the last step if no other errors
      }
    }

    setErrors(allErrors);

    if (firstErrorStep !== null) {
      setActiveStep(firstErrorStep); // Navigate to the first step with errors
      return false;
    }

    return true; // No errors found
  };

  const handleNext = () => {
    if (activeStep === Steps.length - 1) {
      if (validateAllSteps()) {
        submitForm();
      }
    }
    else if (validateActiveStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const findFieldConfig = (fieldName) => {
    for (const step of Steps) {
      const field = step.fields.find(f => f.name === fieldName);
      if (field) {
        return field;
      }
    }
    return null;
  };

  const submitForm = async () => {
    setSubmittingForm(true);
    try {
      // Filter out fields whose dependencies are not satisfied
      const convertedFormData = { ...formData };
      Steps.forEach((step) => {
        step.fields.forEach((field) => {
          if (field.dependency) {
            const dependencyValue = formData[field.dependency.field];
            if (dependencyValue !== field.dependency.value) {
              delete convertedFormData[field.name]; // Exclude the dependent field
            }
          }
        });
      });

      const userData = {};
      const evalData = {};
      for (const fieldName in convertedFormData) {

        convertedFormData[fieldName] = getFieldValue(fieldName);

        const fieldConfig = findFieldConfig(fieldName);
        if (fieldConfig && fieldConfig.numberType !== undefined && typeof convertedFormData[fieldName] === "string") {
          convertedFormData[fieldName] = parseFloat(convertedFormData[fieldName].replace(",", "."));
        }
        if (fieldConfig && fieldConfig.trim) {
          convertedFormData[fieldName] = convertedFormData[fieldName].trim();
        }

        if (fieldConfig.collections.includes("user")) userData[fieldName] = convertedFormData[fieldName];
        if (fieldConfig.collections.includes("eval")) evalData[fieldName] = convertedFormData[fieldName];
      }
      evalData.basalInsulinPercentage = basalInsulinPercentage;

      if (isAddingPatient) {
        const newPatientId = await addPatient(userData, evalData);
        navigate(`/patient/view/${newPatientId}`);
      }
      else {
        const updatedPatient = await updatePatient(patientId, patientRole, userData, evalData, phase);
        if (updatedPatient) {
          const countdownStartedAtChanged =
            isCountdownStartedAtRequired &&
            initialCountdownStartedAt !== userData.countdownStartedAt;

          if (countdownStartedAtChanged && patientForMail) {
            await sendResetCountdownDaysEmail({
              ...patientForMail,
              countdownStartedAt: userData.countdownStartedAt,
            });
          }

          navigate(`/patient/view/${patientId}`);
        }
      }
    }
    catch (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        submitForm: error.message || "Error al enviar el formulario"
      }));
      setSubmittingForm(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onFieldChange = useCallback((event) => {
    const { name, value } = event.target;

    const fieldConfig = Steps[activeStep].fields.find(field => field.name === name);

    if (fieldConfig) {
      const { numberType, maxValue } = fieldConfig;
      switch (numberType) {
        case NumberType.Integer:
          if (!integerPattern.test(value)) return;
          break;
        case NumberType.OneDecimal:
          if (!oneDecimalPattern.test(value)) return;
          break;
        case NumberType.TwoDecimals:
          if (!twoDecimalsPattern.test(value)) return;
          break;
      }

      if (numberType !== undefined && typeof value === "string") {
        if (value !== "0" && value.startsWith("0")) return;
        const numericValue = parseFloat(value.replace(",", "."));
        if (maxValue !== undefined && numericValue > maxValue) return;
      }
    }

    // Handle resetting dependent fields dynamically
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, [name]: value };

      // Check for dependent fields and reset them if their dependency is not satisfied
      Steps.forEach((step) => {
        step.fields.forEach((field) => {
          if (field.dependency && field.dependency.field === name) {
            const dependencySatisfied = value === field.dependency.value;
            if (!dependencySatisfied) {
              updatedFormData[field.name] = ""; // Reset the dependent field
            }
          }
        });
      });

      // Handle specific dependent fields (existing logic)
      const dependentFields = ["dateOfBirth", "leftHand1", "rightHand1", "leftHand2", "rightHand2"];
      if (dependentFields.includes(name)) {
        updatedFormData.muscleStrength = "";
        updatedFormData.percentile = 0;
      }

      return updatedFormData;
    });
  }, [activeStep]);

  const validateActiveStep = () => {
    const newErrors = {};
    const result = validateStep(activeStep, newErrors);
    setErrors(newErrors);
    return result;
  };

  const validateStep = (stepIndex, newErrors) => {
    switch (stepIndex) {
      case 0:
        validateName(formData.firstName, formData.lastName, newErrors);
        validateNickname(formData.nickname, newErrors);
        validateEmail(formData.email, formData.confirmEmail, newErrors);
        validateDate(formData.dateOfBirth, "dateOfBirth", getMinDOB(), newErrors);
        if (mode === PatientDataMode.Edit && isCountdownStartedAtRequired) {
          validateDate(formData.countdownStartedAt, "countdownStartedAt", userCreatedAt, newErrors);
        }
        break;
      case 2:
        validateDate(formData.diseaseOnsetDate, "diseaseOnsetDate", formData.dateOfBirth || getMinDOB(), newErrors);
        validateDatesEquivalence(formData.dateOfBirth, formData.diseaseOnsetDate, newErrors);
        break;
      case 3:
        validateDate(formData.dateOfBirth, "dateOfBirth", getMinDOB(), newErrors);
        validateRequiredField(Steps[0].fields.find(f => f.name === "dateOfBirth"), formData, newErrors);
        validateRequiredField(Steps[0].fields.find(f => f.name === "gender"), formData, newErrors);
        break;
    }

    const requiredFields = Steps[stepIndex].fields.filter((field) => {
      if (field.name !== "countdownStartedAt") return true;
      return isCountdownStartedAtRequired;
    });
    validateRequiredFields(requiredFields, formData, newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const calculateMuscleStrength = async () => {
    if (validateActiveStep()) {
      try {
        setFetchingCentileRank(true);
        onFieldChange({target: {name: "percentile", value: 0}});
        onFieldChange({target: {name: "muscleStrength", value: ""}});

        const age = calculateAge(new Date(formData.dateOfBirth), true);
        const avgHandGripStrength = calculateAvgHandGripStrength();
        const centileRank = await determineCentileRank(age, formData.gender, avgHandGripStrength);
        const muscleStrength = centileRankToMuscleStrength(centileRank);

        setFetchingCentileRank(false);
        onFieldChange({target: {name: "percentile", value: centileRank}});
        onFieldChange({target: {name: "muscleStrength", value: muscleStrength}});
      }
      catch (error) {
        setFetchingCentileRank(false);
        setErrors((prevErrors) => ({
          ...prevErrors,
          calculateMuscleStrength: error.message || "Error al calcular la fuerza muscular"
        }));
      }
    }
  };

  const calculateAvgHandGripStrength = () => {
    const leftHand1 = parseFloat(formData.leftHand1.replace(",", "."));
    const rightHand1 = parseFloat(formData.rightHand1.replace(",", "."));
    const leftHand2 = parseFloat(formData.leftHand2.replace(",", "."));
    const rightHand2 = parseFloat(formData.rightHand2.replace(",", "."));

    if (leftHand1 === 0 || rightHand1 === 0 || leftHand2 === 0 || rightHand2 === 0) {
      throw new Error("Las mediciones no pueden ser cero");
    }

    return (leftHand1 + rightHand1 + leftHand2 + rightHand2) / 4;
  };

  const calculateAvgMaxLeftRightHands = () => {
    const leftHand1 = parseFloat(formData.leftHand1.replace(",", "."));
    const rightHand1 = parseFloat(formData.rightHand1.replace(",", "."));
    const leftHand2 = parseFloat(formData.leftHand2.replace(",", "."));
    const rightHand2 = parseFloat(formData.rightHand2.replace(",", "."));
    const maxLeftHand = Math.max(leftHand1, leftHand2);
    const maxRightHand = Math.max(rightHand1, rightHand2);
    return (maxLeftHand + maxRightHand) / 2;
  };

  const getFieldValue = (name) => {
    switch (name) {
      case "maturation":
        return maturation ? maturation.toFixed(2).replace(".", ",") : "";
      case "phv":
        return phv ? phv.toFixed(2).replace(".", ",") : "";
      case "insulinCorrection":
        return insulinCorrection;
      case "totalInsulinDose":
        return totalInsulinDose;
      case "basalInsulinDistribution":
        return basalInsulinPercentage ? `Basal: ${basalInsulinPercentage}% - Bolo: ${100 - basalInsulinPercentage}%` : "";
      case "age":
        const age = calculateAge(new Date(formData.dateOfBirth), true);
        return age && formData.muscleStrength ? age.toFixed(2).replace(".", ",") : "";
      case "avgMaxLeftRightHands":
        const avgMaxLeftRightHands = calculateAvgMaxLeftRightHands();
        return avgMaxLeftRightHands && formData.muscleStrength ? avgMaxLeftRightHands.toFixed(2).replace(".", ",") : "";
      case "percentile":
        return formData.percentile ? `${formData.percentile}%` : "";
      default:
        return formData[name];
    }
  };

  const renderFormFieldWithName = (fieldName) => {
    if (activeStep < 0) return null;
    const field = Steps[activeStep].fields.find(f => f.name === fieldName);
    if (!field) return null;
    return renderFormField(field, true);
  };

  const getMinDate = (name) => {
    switch (name) {
      case "dateOfBirth":
        return getMinDOB();
      case "countdownStartedAt":
        return userCreatedAt;
      default:
        return formData.dateOfBirth || getMinDOB();
    }
  };

  const renderFormField = (field, ignoreSkip) => {
    const { name, label, type, options, helperText, readOnly: isReadOnlyField, skip, dependency, onlyEditMode } = field;
    if (skip && !ignoreSkip) return null;
    if (onlyEditMode && mode !== PatientDataMode.Edit) return null;
    if (name === "countdownStartedAt" && !isCountdownStartedAtRequired) return null;

    if (dependency) {
      const dependencyValue = formData[dependency.field];
      if (dependencyValue !== dependency.value) return null;
    }

    const forceReadOnly = mode === PatientDataMode.Eval && phase === Phase.POST && !field.collections.includes("eval");

    return (
      <FormField
        key={name}
        label={label}
        name={name}
        type={type}
        value={getFieldValue(name)}
        onChange={onFieldChange}
        readOnly={isReadOnlyField || forceReadOnly}
        error={errors[name]}
        helperText={helperText}
        options={options}
        minDate={getMinDate(name)}
      />
    );
  };

  const onBackClick = () => {
    setOpenConfirmDialog(true);
  };

  const onConfirmBackClick = () => {
    if (isAddingPatient) {
      navigate("/");
    }
    else {
      navigate(`/patient/view/${patientId}`);
    }
  };

  if (!checkedTutorials || !loadedData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={45} />
      </Box>
    );
  }

  if (showTutorialsMessage) {
    return (
      <Container maxWidth="md" sx={{ pt: 6 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Box sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
            <Typography variant="h4" gutterBottom>
              Tutoriales Pendientes
            </Typography>
            <OndemandVideo color="primary" sx={{ width: 60, height: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Completa todos los tutoriales para poder añadir pacientes.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/tutorials")}
              sx={{ mt: 3 }}
            >
              Ir a Tutoriales
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onBackClick} aria-label="back">
            <ArrowBack color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          <Typography variant="h5" align="center" sx={{ flexGrow: 1 }}>
            {getTitle(phase, mode)}
          </Typography>
        </Box>
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
          {Steps.map((step, stepIndex) => (
            <Step key={stepIndex}>
              <StepLabel
                onClick={() => setActiveStep(stepIndex === activeStep ? -1 : stepIndex)}
                style={{ cursor: "pointer" }}
                slots={{stepIcon: StepIcon}}
              >
                {step.title}
              </StepLabel>
              <StepContent>
                <form>
                  {
                    Steps[stepIndex].fields.map(field => renderFormField(field, false))
                  }
                  {stepIndex === 3 && (
                    <>
                      {renderFormFieldWithName("dominantHand")}
                      <FormTitle label="Medición 1" />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {renderFormFieldWithName("leftHand1")}
                        {renderFormFieldWithName("rightHand1")}
                      </Box>
                      <FormTitle label="Medición 2" />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {renderFormFieldWithName("leftHand2")}
                        {renderFormFieldWithName("rightHand2")}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="contained"
                          onClick={calculateMuscleStrength}
                          sx={{ mt: 1, mb: 1 }}
                          disabled={fetchingCentileRank || submittingForm}
                        >
                          Calcular
                        </Button>
                        {fetchingCentileRank && <CircularProgress size={30} />}
                      </Box>
                      {
                        errors.calculateMuscleStrength &&
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          {errors.calculateMuscleStrength}
                        </Typography>
                      }
                      <FormTitle label="Resultados" />
                      {renderFormFieldWithName("age")}
                      {renderFormFieldWithName("avgMaxLeftRightHands")}
                      {renderFormFieldWithName("percentile")}
                      {renderFormFieldWithName("muscleStrength")}
                    </>
                  )}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1, mr: 1 }}
                      disabled={submittingForm}
                    >
                      {activeStep === Steps.length - 1 ? "Guardar" : "Siguiente"}
                    </Button>
                    {
                      !submittingForm &&
                      <Button
                        onClick={handleBack}
                        sx={{ mt: 1 }}
                        disabled={activeStep === 0}
                      >
                        Atrás
                      </Button>
                    }
                    {submittingForm && <CircularProgress size={30} />}
                  </Box>
                  {
                    Object.keys(errors).length > 0 &&
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      Hay errores en el formulario. Por favor, corrígelos antes de continuar.
                    </Typography>
                  }
                  {
                    errors.submitForm &&
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {errors.submitForm}
                    </Typography>
                  }
                </form>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <ConfirmDialog
        open={openConfirmDialog}
        title="Confirmar salida"
        description="¿Seguro que quieres salir? Los cambios no se guardarán."
        onCancel={() => setOpenConfirmDialog(false)}
        onConfirm={onConfirmBackClick}
        confirmButtonColor="error"
      />
    </Container>
  );
};

const getTitle = (phase, mode) => {
  if (phase === Phase.PRE && mode === PatientDataMode.Eval) return "Añadir Paciente";
  if (phase === Phase.PRE && mode === PatientDataMode.Edit) return "Editar Evaluación Pre";

  if (phase === Phase.POST && mode === PatientDataMode.Eval) return "Evaluación Post";
  if (phase === Phase.POST && mode === PatientDataMode.Edit) return "Editar Evaluación Post";
};

export default SetPatientData;